// React
import { useEffect, useRef, useState } from "react"

// Types
import type { SuccessFetch } from "types"
import type { DataType, FetchType } from "../../types"

// Utils
import { useApi } from "utils"

function mapIngredientsPayload(payload: FetchType): DataType {
  return {
    ...payload,
    ingredient: payload.ingredient.map((item) => ({
      ...item,
      price_unit_convention: Number(item.price_unit_convention),
      qty: Number(item.qty),
      soh: Number(item.soh),
    })),
  }
}

function fetchIngredients(
  api: ReturnType<typeof useApi>,
  productId: number,
  locationId?: number
): Promise<DataType | null> {
  const params: { product_id: number; location_id?: number } = {
    product_id: productId,
  }

  if (locationId != null && !Number.isNaN(locationId)) {
    params.location_id = locationId
  }

  return api
    .get("/list_build_item/ingredients", { params })
    .then((res: SuccessFetch<{ payload: FetchType[] }>) => {
      const payload = res.data.payload[0]
      if (!payload) return null
      return mapIngredientsPayload(payload)
    })
}

export function useDetail(
  product_id?: string,
  location_id?: string
): {
  data: DataType | null
  isLoading: boolean
  isRefetching: boolean
} {
  const api = useApi()
  const [data, setData] = useState<DataType | null>(null)
  const [isLoading, setLoading] = useState(true)
  const [isRefetching, setRefetching] = useState(false)
  const initialLoaded = useRef(false)

  const parsedProductId = product_id ? parseInt(product_id, 10) : NaN
  const parsedLocationId = location_id ? parseInt(location_id, 10) : NaN
  const hasValidLocation =
    !!location_id && !Number.isNaN(parsedLocationId)

  useEffect(() => {
    if (!product_id || Number.isNaN(parsedProductId)) {
      setData(null)
      setLoading(false)
      initialLoaded.current = false
      return
    }

    let cancelled = false
    setLoading(true)
    initialLoaded.current = false

    fetchIngredients(api, parsedProductId)
      .catch(() => fetchIngredients(api, parsedProductId, 0))
      .then((result) => {
        if (cancelled) return
        setData(result)
        initialLoaded.current = true
      })
      .catch(() => {
        if (!cancelled) setData(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product_id])

  useEffect(() => {
    if (
      !product_id ||
      Number.isNaN(parsedProductId) ||
      !hasValidLocation ||
      !initialLoaded.current
    ) {
      return
    }

    let cancelled = false
    setRefetching(true)

    fetchIngredients(api, parsedProductId, parsedLocationId)
      .then((result) => {
        if (cancelled) return
        if (result) setData(result)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setRefetching(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id])

  return { data, isLoading, isRefetching }
}
